// Lightweight localStorage-backed cart for buyer flow
// ────────────────────────────────────────────────────────────────────────────
// Each cart item: { product_id, name, qty, price, seller_id, seller_name, image?, moq?, tiers?, map_price? }
// `price` is the BASE price; `tiers` (optional) is an array of {min_qty, price}.
// Use `effectivePrice(item)` to get the tier-adjusted per-unit price for the
// current qty. This keeps stored data minimal while still letting us re-price
// at render/checkout time.
// Stored under key `verqo_cart` so it survives page reloads / nav.

const KEY = "verqo_cart";

export function effectivePrice(item) {
  // Pick the highest tier whose min_qty <= current qty; fall back to base.
  const tiers = item.tiers || [];
  if (!tiers.length) return Number(item.price) || 0;
  let best = Number(item.price) || 0;
  let bestQty = -1;
  for (const t of tiers) {
    const mq = Number(t.min_qty);
    if (Number.isFinite(mq) && item.qty >= mq && mq > bestQty) {
      bestQty = mq;
      best = Number(t.price) || best;
    }
  }
  return best;
}

export function getCart() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function setCart(items) {
  localStorage.setItem(KEY, JSON.stringify(items));
  // notify listeners on this tab
  window.dispatchEvent(new CustomEvent("verqo-cart-change"));
}

export function addToCart(item) {
  const cur = getCart();
  const ex = cur.find(x => x.product_id === item.product_id);
  if (ex) {
    ex.qty += item.qty;
    // If newer payload has tiers/map info we didn't have before, merge.
    if (item.tiers && !ex.tiers) ex.tiers = item.tiers;
    if (item.map_price != null && ex.map_price == null) ex.map_price = item.map_price;
    setCart(cur);
  } else {
    setCart([...cur, item]);
  }
}

export function clearCart() { setCart([]); }
export function removeFromCart(pid) { setCart(getCart().filter(x => x.product_id !== pid)); }
export function removeCartItemsForSeller(sellerId) {
  setCart(getCart().filter(x => x.seller_id !== sellerId));
}
export function updateQty(pid, qty) {
  const cur = getCart();
  const it = cur.find(x => x.product_id === pid);
  if (it) { it.qty = Math.max(1, qty); setCart(cur); }
}
export function cartCount() { return getCart().reduce((s, x) => s + x.qty, 0); }
export function cartTotal() { return getCart().reduce((s, x) => s + x.qty * effectivePrice(x), 0); }
